import { UploadItem } from '@wellesley/uploads'
import { Progress } from '@wellesley/ui'
import { X, Check, AlertCircle } from 'lucide-react'

type UploadPreviewItemProps = {
  item: UploadItem
  isCompleted: boolean
  isError: boolean
}

type UploadStatus = 'uploading' | 'completed' | 'error'

const getUploadStatus = (
  isCompleted: boolean,
  isError: boolean,
): UploadStatus => {
  if (isError) return 'error'
  if (isCompleted) return 'completed'
  return 'uploading'
}

const StatusOverlay = ({
  status,
  progress,
}: {
  status: UploadStatus
  progress: number
}) => {
  switch (status) {
    case 'uploading':
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="w-12">
            <Progress value={progress} className="h-1" />
          </div>
        </div>
      )
    case 'completed':
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="rounded-full bg-green-500 p-1">
            <Check className="h-4 w-4 text-white" />
          </div>
        </div>
      )
    case 'error':
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="rounded-full bg-red-500 p-1">
            <AlertCircle className="h-4 w-4 text-white" />
          </div>
        </div>
      )
  }
}

export const UploadPreviewItem = ({
  item,
  isCompleted,
  isError,
}: UploadPreviewItemProps) => {
  const status = getUploadStatus(isCompleted, isError)
  const canCancel = status === 'uploading' && item.cancel

  return (
    <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
      {item.preview && (
        <img
          src={item.preview}
          alt="Upload preview"
          className="h-full w-full object-cover"
        />
      )}

      <StatusOverlay status={status} progress={item.progress} />

      {item.type === 'VIDEO' && (
        <div className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-xs text-white">
          Video
        </div>
      )}

      {canCancel && (
        <button
          onClick={() => item.cancel()}
          className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 hover:bg-black/80"
        >
          <X className="h-3 w-3 text-white" />
        </button>
      )}
    </div>
  )
}
